import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useWorkflowStatus } from '../contexts/WorkflowContext.jsx';

/**
 * GlobalWorkflowIndicator
 * A small, read-only global indicator that shows when a background workflow is active.
 *
 * ARCHITECTURE (WebSocket-ready):
 * - This component NO LONGER polls - it subscribes to WorkflowContext
 * - WorkflowContext is updated by useArchitectApp (single source of polling)
 * - When WebSocket is implemented, useArchitectApp will push updates via WebSocket
 *   instead of polling, and this component will work without changes
 *
 * Design goals:
 * - Non-intrusive: fixed pill in the top-right corner (or inline in nav)
 * - Read-only: never starts/stops workflows (no side effects)
 * - Zero overhead: no polling, just context subscription
 * - Hidden when main workflow UI is visible (Home page in workflow mode)
 */
export default function GlobalWorkflowIndicator({ inline = false } = {}) {
    const location = useLocation();
    const {
        isActive,
        isComplete,
        isQueueMode,
        workflowId,
        status,
        currentStep,
        stepName,
        progress,
        loadingTitle,
        batchProgress,
        shouldHideGlobalIndicator,
        // 🔄 UNIFIED: Queue fields
        queuePosition,
        queueEstimatedWait,
        // 🚀 DEPLOYMENT: Deployment fields
        isDeploymentMode,
        deploymentStatus,
        deploymentPhase,
        deploymentFilesCount,
        deploymentSessionId,
        deploymentMode, // 'vibe' or 'deploy'
    } = useWorkflowStatus();

    // Calculate progress percentage
    const percent = React.useMemo(() => {
        if (progress?.percentComplete != null) {
            const p = Number(progress.percentComplete);
            return Number.isFinite(p) ? Math.max(0, Math.min(100, Math.round(p))) : null;
        }
        if (batchProgress?.targetPrompts && batchProgress?.completedPrompts != null) {
            const { completedPrompts, targetPrompts } = batchProgress;
            if (targetPrompts > 0) {
                return Math.max(0, Math.min(100, Math.round((completedPrompts / targetPrompts) * 100)));
            }
        }
        return null;
    }, [progress, batchProgress]);

    // Hide conditions:
    // 1. No active workflow/queue/deployment
    // 2. If NOT inline: Context says to hide (another component is showing full workflow UI)
    // Note: When inline=true (in Navigation), ALWAYS show the indicator for consistency
    // The shouldHideGlobalIndicator only affects the floating/fixed position indicator
    // 🚀 Also hide if on Deploy/Vibe page and deployment is active (those pages show their own UI)
    const onDeployPage = location.pathname.startsWith('/deploy');
    const onVibePage = location.pathname.startsWith('/vibe');
    const shouldHide = !isActive || (!inline && shouldHideGlobalIndicator) || (isDeploymentMode && (onDeployPage || onVibePage));

    if (shouldHide) {
        return null;
    }

    const stepLabel = stepName || loadingTitle || 'Processing';
    const shortId = (workflowId || '').slice(-6);
    // 🔧 Navigate to Home (/) where EnhancedAnalysisLoader shows progress
    // For completed workflows, go to /analyze to view results
    const isTerminal = ['completed', 'failed', 'cancelled'].includes(status);
    const viewHref = isTerminal ? `/analyze?tab=overview&skipinit=1` : `/`;

    // 🚀 DEPLOYMENT: Determine indicator mode (priority: deployment > queue > workflow)
    const getIndicatorConfig = () => {
        if (isDeploymentMode) {
            // Map deployment status to human-readable phase
            const phaseLabels = {
                'initialized': 'Starting...',
                'generating': `Generating (${deploymentFilesCount || 0} files)`,
                'building': 'Building...',
                'paused': 'Paused',
                'preview': 'Preview Ready',
                'deploying': 'Deploying...',
                'deployed': 'Deployed!',
            };
            const phaseLabel = phaseLabels[deploymentStatus] || deploymentPhase || deploymentStatus || 'Processing';

            // 🔧 FIX: Route to correct page based on deployment mode
            const isVibeMode = deploymentMode === 'vibe';
            const href = isVibeMode ? `/vibe/${deploymentSessionId}` : `/deploy/${deploymentSessionId}`;

            return {
                gradient: isVibeMode
                    ? 'from-cyan-500/10 to-teal-500/10 border-cyan-500/30'
                    : 'from-orange-500/10 to-amber-500/10 border-orange-500/30',
                dotColor: isVibeMode ? 'bg-cyan-500' : 'bg-orange-500',
                textColor: isVibeMode ? 'text-cyan-200/70' : 'text-orange-200/70',
                buttonBg: isVibeMode
                    ? 'bg-cyan-500/30 text-cyan-200 hover:bg-cyan-500/40'
                    : 'bg-orange-500/30 text-orange-200 hover:bg-orange-500/40',
                title: isVibeMode ? 'Vibe Coding' : 'Deployment Active',
                subtitle: phaseLabel,
                href,
            };
        }
        if (isQueueMode) {
            return {
                gradient: 'from-amber-500/10 to-orange-500/10 border-amber-500/30',
                dotColor: 'bg-amber-500',
                textColor: 'text-amber-200/70',
                buttonBg: 'bg-amber-500/30 text-amber-200 hover:bg-amber-500/40',
                title: `In Queue #${queuePosition}`,
                subtitle: `~${queueEstimatedWait} min wait`,
                href: `/`,
            };
        }
        // Default: Workflow mode
        return {
            gradient: 'from-[#7B61FF]/10 to-[#BA87FF]/10 border-[#7B61FF]/30',
            dotColor: 'bg-[#7B61FF]',
            textColor: 'text-[#CCCCCC]',
            buttonBg: 'bg-[#7B61FF]/30 text-[#BA87FF] hover:bg-[#7B61FF]/40',
            title: 'Workflow running',
            subtitle: `Step ${currentStep || 1} · ${stepLabel}${shortId ? ` · ID …${shortId}` : ''}`,
            href: viewHref,
        };
    };

    const config = getIndicatorConfig();

    // 🔄 UNIFIED: Show queue, workflow, or deployment indicator
    return (
        <div className={inline ? "ml-3 inline-flex items-center" : "fixed top-4 right-4 z-[9999]"}>
            <div className={`bg-gradient-to-r ${config.gradient} border rounded-lg shadow-lg`}>
                <div className="px-3 py-2 flex items-center gap-2">
                    <span className={`inline-flex w-2.5 h-2.5 rounded-full ${config.dotColor} animate-pulse`} />
                    <div className="text-sm text-white/90">
                        <div className="font-medium">{config.title}</div>
                        <div className={`text-[11px] ${config.textColor}`}>{config.subtitle}</div>
                    </div>
                    {!isQueueMode && !isDeploymentMode && percent != null && !isTerminal && (
                        <div className="ml-2 text-xs text-[#BA87FF] font-semibold">{percent}%</div>
                    )}
                    <Link
                        to={config.href}
                        className={`ml-2 px-2 py-1 text-xs rounded ${config.buttonBg} transition-colors`}
                    >
                        {isDeploymentMode ? 'View' : (isTerminal ? 'Open' : 'View')}
                    </Link>
                </div>
            </div>
        </div>
    );
}
